import { useCallback, useEffect, useRef, useState } from 'react';
import { theme } from '@frontend/theme';
import { useImagePreviewValues } from '../provider/image-preview.provider';

const applyImagePlacement = ({
  imgEl,
  containerEl,
  bodyWidth,
  bodyHeight,
}: {
  imgEl: HTMLImageElement;
  containerEl: HTMLDivElement;
  bodyWidth: number;
  bodyHeight: number;
}) => {
  const imgW = imgEl.offsetWidth;
  const imgH = imgEl.offsetHeight;
  if (!imgW || !imgH) return;

  const bodyAR = bodyWidth / bodyHeight;
  const imgAR = imgW / imgH;
  const isWide = imgAR >= bodyAR;

  if (isWide) {
    containerEl.style.width = '100%';
    containerEl.style.height = '';
    imgEl.style.width = '100%';
    imgEl.style.height = 'auto';
  } else {
    containerEl.style.width = '';
    containerEl.style.height = '100%';
    imgEl.style.height = '100%';
    imgEl.style.width = 'auto';
  }

  return { isWide };
};

const applyZoomScaling = ({
  imgEl,
  containerEl,
  isWide,
  zoomValue,
  bodyHeight,
  bodyWidth,
}: {
  imgEl: HTMLImageElement;
  containerEl: HTMLDivElement;
  isWide: boolean;
  zoomValue: number;
  bodyHeight: number;
  bodyWidth: number;
}) => {
  requestAnimationFrame(() => {
    const finalW = imgEl.offsetWidth;
    const finalH = imgEl.offsetHeight;
    const scaleValue = isWide ? Math.min(finalH * zoomValue, bodyHeight) : Math.min(finalW * zoomValue, bodyWidth);

    if (isWide) {
      containerEl.style.height = `${scaleValue}px`;
    } else {
      containerEl.style.width = `${scaleValue}px`;
    }
  });
  containerEl.style.visibility = 'visible';
};

export const ImageContent = () => {
  const { src, alt, zoomValue, rotationDegree, bodyDimensions } = useImagePreviewValues();
  const [isPanning, setIsPanning] = useState(false);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isReady, setIsReady] = useState(false);

  const translateRef = useRef(translate);
  const panOriginRef = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const { width: bodyWidth, height: bodyHeight } = bodyDimensions ?? {};

  useEffect(() => {
    const imgEl = imgRef.current;
    const containerEl = containerRef.current;
    const isBodyReady = bodyWidth && bodyHeight;
    if (!imgEl || !containerEl || !isBodyReady) return;

    const result = applyImagePlacement({
      imgEl,
      containerEl,
      bodyWidth,
      bodyHeight,
    });

    if (!result) return;

    applyZoomScaling({
      imgEl,
      containerEl,
      isWide: result.isWide,
      zoomValue,
      bodyHeight,
      bodyWidth,
    });
    setIsReady(true);
  }, [bodyWidth, bodyHeight, zoomValue]);

  useEffect(() => {
    setTranslate({ x: 0, y: 0 });
    translateRef.current = { x: 0, y: 0 };
  }, [zoomValue, rotationDegree]);

  const handlePanMove = useCallback(
    (event: MouseEvent) => {
      const newX = event.clientX - panOriginRef.current.x;
      const newY = event.clientY - panOriginRef.current.y;

      const container = containerRef.current;
      const img = imgRef.current;
      if (!container || !img) return;

      const containerRect = container.getBoundingClientRect();
      const unscaledImageWidth = img.offsetWidth;
      const unscaledImageHeight = img.offsetHeight;

      const scaledWidth = unscaledImageWidth * zoomValue;
      const scaledHeight = unscaledImageHeight * zoomValue;
      const maxX = Math.max(0, (scaledWidth - containerRect.width) / 2);
      const maxY = Math.max(0, (scaledHeight - containerRect.height) / 2);
      const clampedX = Math.min(Math.max(newX, -maxX), maxX);
      const clampedY = Math.min(Math.max(newY, -maxY), maxY);

      const updated = { x: clampedX, y: clampedY };
      translateRef.current = updated;
      setTranslate(updated);
    },
    [zoomValue]
  );

  const handlePanEnd = useCallback(() => {
    setIsPanning(false);
    removeEventListener('mousemove', handlePanMove);
    removeEventListener('mouseup', handlePanEnd);
  }, [handlePanMove]);

  const handlePanStart = useCallback(
    (event: React.MouseEvent<HTMLImageElement>) => {
      if (zoomValue <= 1) return;
      event.preventDefault();
      setIsPanning(true);
      panOriginRef.current = {
        x: event.clientX - translateRef.current.x,
        y: event.clientY - translateRef.current.y,
      };
      addEventListener('mousemove', handlePanMove);
      addEventListener('mouseup', handlePanEnd);
    },
    [zoomValue, handlePanMove, handlePanEnd]
  );

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        borderRadius: theme.borderRadius.medium,
        visibility: isReady ? 'visible' : 'hidden',
      }}
    >
      <img
        src={src}
        alt={alt}
        ref={imgRef}
        style={{
          display: 'block',
          objectFit: 'contain',
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${zoomValue}) rotate(${rotationDegree}deg)`,
          transition: isPanning ? 'none' : 'transform 0.1s ease',
          borderRadius: theme.borderRadius.medium,
          cursor: zoomValue > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default',
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={handlePanStart}
      />
    </div>
  );
};

ImageContent.displayName = 'ImageContent';
